import { AnalyticsBrowser } from '@segment/analytics-next';

import config from '@/config';
import {
	Tracker,
	TrackingEvent,
	TrackerLogger,
	TrackingData,
} from '../Tracking.events';

// TODO: Hardcoded for now. This will be provided through the analytics
// configuration object (e.g. `config.analytics.segment.writeKey`) later.
export const SEGMENT_WRITE_KEY = config.analytics?.segment?.writeKey || '';

// Single Segment analytics-next instance shared across the app. The
// AnalyticsBrowser returned by `load` buffers calls until the SDK is ready, so
// it's safe to call track/page/identify right after `init`.
let analytics: AnalyticsBrowser | null = null;

/**
 * Twilio Segment CDP tracker.
 *
 * Wraps `@segment/analytics-next` and maps the app's tracking events to
 * Segment `track`/`page`/`identify` calls.
 */
export class SegmentTracker implements Tracker {
	writeKey: string = '';
	constructor(writeKey: string | null) {
		if (!writeKey) return;
		TrackerLogger.info(`Segment running with write key:`, writeKey);
		this.writeKey = writeKey;
	}

	sendEvent(event: TrackingEvent) {
		TrackerLogger.debug(`Event in segment:`, event);
		const { type } = event;
		switch (type) {
			case 'INSTALLED':
				this.onInstalled();
				break;
			case 'LOGIN':
				this.onLogin();
				break;
			case 'OPEN_TOC':
				this.onOpenToc();
				break;
			case 'CLOSE_TOC':
				this.onCloseToc();
				break;
			case 'SHARE':
				this.onShare(event.data);
				break;
			case 'BTN_CLICK_INSTALL':
				this.onClickInstall();
				break;
			case 'BTN_CLICK_LOGIN':
				this.onClickLogIn();
				break;
			case 'BTN_CLICK_LOGOUT':
				this.onClickLogOut();
				break;
			case 'BTN_CLICK_RESPONSIVE':
				this.onClickResponsive(event.data);
				break;
			case 'BTN_CLICK_REPLICA':
				this.onClickReplica(event.data);
				break;
			case 'BTN_CLICK_REPLICA_SINGLE':
				this.onClickReplicaSingle(event.data);
				break;
			case 'BTN_CLICK_REPLICA_DOUBLE':
				this.onClickReplicaDouble(event.data);
				break;
			case 'BTN_CLICK_FAVORITE_ARTICLE':
				this.onClickFavoriteArticle(event.data);
				break;
			case 'BTN_CLICK_UNFAVORITE_ARTICLE':
				this.onClickUnfavoriteArticle(event.data);
				break;
			case 'BTN_CLICK_ISSUE':
				this.onClickIssue(event.data);
				break;
			case 'LANGUAGE_CHANGE':
				this.onChangeLanguage(event.data);
				break;
			default:
				return;
		}
	}

	private track(event: string, properties?: Record<string, unknown>) {
		if (!analytics) {
			TrackerLogger.error(`Segment analytics is not initialized yet`);
			return;
		}
		analytics.track(event, properties);
	}

	private onInstalled() {
		this.track('Installed', { category: 'app' });
	}

	private onLogin() {
		this.track('Login', { category: 'session' });
	}

	private onOpenToc() {
		this.track('Open TOC', { category: 'issue' });
	}

	private onCloseToc() {
		this.track('Close TOC', { category: 'issue' });
	}

	private onShare(data: TrackingData) {
		this.track('Share', {
			category: 'article',
			method: data?.method,
			contentType: data?.contentType,
			itemId: data?.article?.ArticleID,
		});
	}

	private onClickInstall() {
		this.track('Click Install', { category: 'session' });
	}

	private onClickLogIn() {
		this.track('Click Login', { category: 'session' });
	}

	private onClickLogOut() {
		this.track('Click Logout', { category: 'session' });
	}

	private onClickResponsive(data: TrackingData) {
		this.track('Click Responsive', { category: 'issue', ...data });
	}

	private onClickReplica(data: TrackingData) {
		this.track('Click Replica', { category: 'issue', ...data });
	}

	private onClickReplicaSingle(data: TrackingData) {
		this.track('Click Single Page Replica', { category: 'issue', ...data });
	}

	private onClickReplicaDouble(data: TrackingData) {
		this.track('Click Double Page Replica', { category: 'issue', ...data });
	}

	private onClickFavoriteArticle(data: TrackingData) {
		this.track('Favorite Article', { category: 'article', ...data });
	}

	private onClickUnfavoriteArticle(data: TrackingData) {
		this.track('Unfavorite Article', { category: 'article', ...data });
	}

	private onClickIssue(data: TrackingData) {
		this.track('Open Issue', { category: 'navigation', ...data });
	}

	private onChangeLanguage(data: TrackingData) {
		this.track('Change Language', { category: 'article', ...data });
	}

	/**
	 * Loads the Segment analytics-next SDK with the provided write key. Safe to
	 * call multiple times — only the first call creates the instance.
	 */
	static init(writeKey: string = SEGMENT_WRITE_KEY) {
		if (!writeKey) return;
		if (analytics) return;

		analytics = AnalyticsBrowser.load({ writeKey });
		TrackerLogger.info(`Segment analytics-next loaded`);
	}

	/** Associate the current visitor with a known user id in the CDP. */
	static identify(userId?: string, traits?: Record<string, unknown>) {
		if (!analytics) return;
		if (userId) {
			analytics.identify(userId, traits);
		} else {
			analytics.identify(traits);
		}
	}

	/** Send a page view to Segment. */
	static pageView(properties?: Record<string, unknown>) {
		if (!analytics) return;
		analytics.page(properties);
	}
}
